import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Tasks from 'APP/Tasks';
import { OnboardingType } from 'APP/constants/onboarding';
import { SpaceDemoPopup } from 'APP/main/PopupManager/Popups/SpaceDemoPopup/SpaceDemoPopup';
import { generalAnalytics } from 'APP/packages/analytics/analytics';
import { TabType, TopTabType } from 'APP/packages/analytics/general/generalAnalytics.types';
import { Features } from 'APP/packages/features/Features';
import { RouterLink } from 'APP/router/constants';
import Entities from 'STORE';
import { ClientConfigStoreKey } from 'STORE/ClientConfig/ClientConfig';

import { SidebarPanelMode } from '../SidebarPanel.constants';

export interface ISidebarPanelNavigationPresenterParams {
  panelMode: SidebarPanelMode;
  onPanelModeChange(panelMode: SidebarPanelMode): void;
}

interface ISidebarPanelNavigationPresenter {
  currentPath: string;
  panelMode: SidebarPanelMode;
  isCalendarEnabled: boolean;
  isActiveTalks: boolean;
  isActiveCalendar: boolean;
  isActiveSpaces: boolean;
  isShowMarketplace: boolean;
  isShowSpaceBanner: boolean;
  isToAdminButtonShow: boolean;
  initSpaceOnboarding(): void;
  setActiveTalks(): void;
  setActiveSpaces(): void;
  trackTopTabOpened(panelMode: SidebarPanelMode, topTabType: TopTabType): void;
  openCatalog(): void;
  openNewsfeed(): void;
  openHub(): void;
  openSupport(): void;
  setActiveCalendar(): void;
  openAdminPanel(): void;
}

export const useSidebarPanelNavigationPresenter = (
  data: ISidebarPanelNavigationPresenterParams
): ISidebarPanelNavigationPresenter => {
  const { panelMode, onPanelModeChange } = data;
  const location = useLocation();
  const navigate = useNavigate();

  const presenter = useLocalObservable<ISidebarPanelNavigationPresenter>(() => ({
    currentPath: '',
    panelMode,

    get isCalendarEnabled(): boolean {
      return Entities.featuresStore.isCalendarIntegrationEnabled;
    },

    get isActiveCalendar(): boolean {
      return SidebarPanelMode.Calendar === presenter.panelMode;
    },

    get isActiveTalks(): boolean {
      return [SidebarPanelMode.None, SidebarPanelMode.Talks].includes(presenter.panelMode);
    },

    get isActiveSpaces(): boolean {
      return [SidebarPanelMode.Spaces, SidebarPanelMode.Space].includes(presenter.panelMode);
    },

    get isShowMarketplace(): boolean {
      return Entities.marketplace.isAvailable || Features.isHubEnabled();
    },

    get isShowSpaceBanner(): boolean {
      if (!Features.isSpacesEnabled() || !Features.isOnboardingEnabled()) {
        return false;
      }

      return Entities.onboarding.getIsNeedToShow(OnboardingType.SpaceDemo);
    },

    get isToAdminButtonShow(): boolean {
      return !!Entities.clientConfig.getConfig(ClientConfigStoreKey.AdminAuthUrlBase);
    },

    initSpaceOnboarding(): void {
      if (!Features.isSpacesEnabled() || !Features.isOnboardingEnabled()) {
        return;
      }

      Entities.onboarding.addToShow(OnboardingType.SpaceDemo);
    },

    setActiveTalks(): void {
      onPanelModeChange(SidebarPanelMode.Talks);
      presenter.trackTopTabOpened(SidebarPanelMode.Talks, TopTabType.All);
    },

    setActiveCalendar(): void {
      if (presenter.currentPath === RouterLink.Calendar) {
        return;
      }
      onPanelModeChange(SidebarPanelMode.Calendar);
      navigate(RouterLink.Calendar);
      presenter.trackTopTabOpened(SidebarPanelMode.Calendar, TopTabType.Calendar);
    },

    setActiveSpaces(): void {
      if (presenter.isShowSpaceBanner) {
        Tasks.app.openPopup(SpaceDemoPopup);
      }

      onPanelModeChange(SidebarPanelMode.Spaces);
      presenter.trackTopTabOpened(SidebarPanelMode.Spaces, TopTabType.Spaces);
    },

    trackTopTabOpened(panelMode: SidebarPanelMode, topTabType: TopTabType): void {
      if (presenter.panelMode !== panelMode) {
        generalAnalytics.trackTopMenuOpened(topTabType);
      }
    },

    openCatalog(): void {
      if (presenter.currentPath !== RouterLink.Catalog) {
        Entities.catalogStore.reset();
        navigate(RouterLink.Catalog);
        generalAnalytics.trackBottomMenuOpened(TabType.Catalog);
      }
    },

    openNewsfeed(): void {
      if (presenter.currentPath !== RouterLink.Newsfeed) {
        Tasks.newsfeed.goToNewsfeed();
        generalAnalytics.trackBottomMenuOpened(TabType.Feed);
      }
    },

    openHub(): void {
      if (presenter.currentPath !== RouterLink.Hub) {
        navigate(RouterLink.Hub);
        generalAnalytics.trackBottomMenuOpened(TabType.Hub);
      }
    },

    openSupport(): void {
      Tasks.app.openSupport();
    },

    openAdminPanel(): void {
      Tasks.app.openAdminPanel();
    },
  }));

  useEffect(() => {
    presenter.initSpaceOnboarding();
  }, []);

  useEffect(() => {
    presenter.currentPath = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    presenter.panelMode = panelMode;
  }, [panelMode]);

  return presenter;
};
